// src/hooks/useMarketingCampaigns.ts - Version avec usageType et gestion des usages multiples

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  Timestamp,
  increment,
  limit,
  writeBatch,
  deleteField,
} from 'firebase/firestore';
import { auth, db } from '../config/firebase';
import { COLLECTIONS } from '../config/collections';
import { handleFirestoreError } from '../utils/firestoreErrorHandler';
import { matchesSearchInFields } from '../utils/searchUtils';
import { 
  Campaign, 
  ClientPromotion, 
  MarketingDashboardData,
  CampaignFilters,
  getComputedCampaignStatus,
  getRecommendedUsageType
} from '../types/marketing';
import { Client, Prestation } from '../types/entities';
import { generatePromotionsForCampaign } from '../utils/marketingTargeting';
import { getMarketingConfiguration } from '../types/business';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from './usePermissions';
import dayjs from 'dayjs';
import { FirebaseError } from 'firebase/app';
import { useMemo } from 'react';
import { replaceTemplateVariables } from '../constants/messageTemplates';

import { formatCurrency } from '../utils/numberFormat';

// ===== HOOKS POUR LES CAMPAGNES =====

export const useCampaigns = (filters?: CampaignFilters) => {
  const { currentBusiness } = useAuth();
  const permissions = usePermissions();
  const businessId = currentBusiness?.id;

  return useQuery({
    queryKey: ['marketing', 'campaigns', businessId],
    queryFn: async () => {
      if (!businessId) {
        console.warn('Aucun businessId disponible');
        return [];
      }

      try {
        let q = query(
          collection(db, COLLECTIONS.campaigns),
          where('businessId', '==', businessId),
          orderBy('createdAt', 'desc')
        );

        // Appliquer les filtres si nécessaire
        if (filters?.status) {
          q = query(q, where('status', '==', filters.status));
        }

        if (filters?.type) {
          q = query(q, where('type', '==', filters.type));
        }

        // NOUVEAU : Filtre par usageType
        if (filters?.usageType) {
          q = query(q, where('usageType', '==', filters.usageType));
        }

        const snapshot = await getDocs(q).catch(error => {
          handleFirestoreError(error, 'useMarketingCampaigns - Main query');
          throw error;
        });
        
        let campaigns = snapshot.docs.map(doc => {
          const data = doc.data();
          return convertFirestoreToCampaign(doc.id, data);
        }) as Campaign[];

        // Filtrage côté client pour les critères complexes
        if (filters?.searchTerm) {
          campaigns = campaigns.filter(campaign =>
            matchesSearchInFields(
              [campaign.name, campaign.description],
              filters.searchTerm!
            )
          );
        }

        if (filters?.dateRange) {
          campaigns = campaigns.filter(campaign =>
            campaign.createdAt >= filters.dateRange![0] &&
            campaign.createdAt <= filters.dateRange![1]
          );
        }

        return campaigns;
      } catch (error) {
        console.warn('Campaigns collection not ready:', error);
        return [];
      }
    },
    enabled: !!businessId && (permissions?.canViewMarketing ?? false),
    retry: false, 
  });
};

export const useCampaign = (campaignId: string) => {
  const { currentBusiness } = useAuth();
  const permissions = usePermissions();
  const businessId = currentBusiness?.id;

  return useQuery({
    queryKey: ['marketing', 'campaigns', businessId, campaignId],
    queryFn: async () => {
      const docRef = doc(db, COLLECTIONS.campaigns, campaignId);
      const docSnap = await getDoc(docRef);
      
      if (!docSnap.exists()) {
        throw new Error('Campagne introuvable');
      }

      const data = docSnap.data();
      const campaign = convertFirestoreToCampaign(docSnap.id, data);
      
      // SÉCURITÉ : Vérifier l'appartenance à business
      if (campaign.businessId !== businessId) {
        throw new Error('Cette campagne n\'appartient pas à votre business');
      }

      return campaign;
    },
    enabled: !!campaignId && !!businessId && (permissions?.canViewMarketing ?? false),
  });
};

export const useAddCampaign = () => {
  const queryClient = useQueryClient();
  const { currentBusiness, userProfile } = useAuth();
const businessId = currentBusiness?.id;
  
  return useMutation({
    mutationFn: async (campaign: Omit<Campaign, 'id' | 'createdAt' | 'stats' | 'businessId'>) => {
      if (!businessId) {
        throw new Error('Institut non défini');
      }

      // NOUVEAU : Vérifier la limite de campagnes actives avant création
      if (currentBusiness && campaign.status === 'active') {
        const marketingConfig = getMarketingConfiguration(currentBusiness);
        
        if (marketingConfig.maxActiveCampaigns) {
          const campaignsQuery = query(
            collection(db, COLLECTIONS.campaigns),
            where('businessId', '==', businessId),
            where('status', '==', 'active')
          );
          const campaignsSnap = await getDocs(campaignsQuery).catch(error => {
            handleFirestoreError(error, 'useMarketingCampaigns - Campaigns query');
            throw error;
          });
          const activeCampaigns = campaignsSnap.docs.map(doc => convertFirestoreToCampaign(doc.id, doc.data()));
          
          const reallyActiveCampaigns = activeCampaigns.filter(camp => {
            const computedStatus = getComputedCampaignStatus(camp);
            return computedStatus === 'active';
          });

          if (reallyActiveCampaigns.length >= marketingConfig.maxActiveCampaigns) {
            throw new Error(
              `Limite de campagnes actives atteinte (${reallyActiveCampaigns.length}/${marketingConfig.maxActiveCampaigns}). ` +
              'Mettez en pause ou supprimez une campagne existante.'
            );
          }
        }
      }

      // NOUVEAU : Si pas d'usageType fourni, utiliser la recommandation
      const finalUsageType = campaign.usageType || getRecommendedUsageType(campaign.type);

      const campaignData: any = {
        ...campaign,
        businessId,
        usageType: finalUsageType,  // NOUVEAU : Assurer la présence du usageType
        createdBy: userProfile?.id || 'unknown',
        createdAt: Timestamp.now(),
        startDate: Timestamp.fromDate(campaign.startDate),
        stats: {
          messagesSent: 0,
          messagesGenerated: 0,
          conversionsCount: 0,
          revenueGenerated: 0,
        },
        offerDetails: {
          ...campaign.offerDetails,
          endDate: Timestamp.fromDate(campaign.offerDetails.endDate),
        },
        targetCriteria: convertTargetCriteriaToFirestore(campaign.targetCriteria),
      };

      const docRef = await addDoc(collection(db, COLLECTIONS.campaigns), campaignData);
      return docRef.id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['marketing', 'campaigns', businessId] });
      queryClient.invalidateQueries({ queryKey: ['marketing', 'dashboard'] });
    },
  });
};

export const useUpdateCampaign = () => {
  const queryClient = useQueryClient();
  const { currentBusiness } = useAuth();
const businessId = currentBusiness?.id;
  
  return useMutation({
    mutationFn: async ({ id, ...data }: Partial<Campaign> & { id: string }) => {
      // SÉCURITÉ : Vérifier que la campagne appartient à business
      const campaignRef = doc(db, COLLECTIONS.campaigns, id);
      const campaignSnap = await getDoc(campaignRef);
      
      if (!campaignSnap.exists()) {
        throw new Error('Campagne introuvable');
      }
      
      const existingCampaign = campaignSnap.data();
      if (existingCampaign.businessId !== businessId) {
        throw new Error('Cette campagne n\'appartient pas à votre business');
      }

      // NOUVEAU : Vérifier la limite si on active une campagne
      if (currentBusiness && data.status === 'active' && existingCampaign.status !== 'active') {
        const marketingConfig = getMarketingConfiguration(currentBusiness);
        
        if (marketingConfig.maxActiveCampaigns) {
          const campaignsQuery = query(
            collection(db, COLLECTIONS.campaigns),
            where('businessId', '==', businessId),
            where('status', '==', 'active')
          );
          const campaignsSnap = await getDocs(campaignsQuery).catch(error => {
            handleFirestoreError(error, 'useMarketingCampaigns - Campaigns query');
            throw error;
          });
          const activeCampaigns = campaignsSnap.docs
            .filter(doc => doc.id !== id)
            .map(doc => convertFirestoreToCampaign(doc.id, doc.data()));
          
          const reallyActiveCampaigns = activeCampaigns.filter(camp => {
            const computedStatus = getComputedCampaignStatus(camp);
            return computedStatus === 'active';
          });

          if (reallyActiveCampaigns.length >= marketingConfig.maxActiveCampaigns) {
            throw new Error(
              `Limite de campagnes actives atteinte (${reallyActiveCampaigns.length}/${marketingConfig.maxActiveCampaigns}). ` +
              'Mettez en pause ou supprimez une campagne existante.'
            );
          }
        }
      }

      const updateData: any = {
        ...data,
        updatedAt: Timestamp.now(),
      };

      // Supprimer businessId des updates (sécurité)
      delete updateData.businessId;

      if (data.startDate) {
        updateData.startDate = Timestamp.fromDate(data.startDate);
      }

      if (data.offerDetails) {
        updateData.offerDetails = {
          ...data.offerDetails,
          endDate: Timestamp.fromDate(data.offerDetails.endDate),
        };
      }

      if (data.targetCriteria) {
        updateData.targetCriteria = convertTargetCriteriaToFirestore(data.targetCriteria);
      }

      await updateDoc(campaignRef, updateData);
      return { id, ...data };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['marketing', 'campaigns', businessId] });
      queryClient.invalidateQueries({ queryKey: ['marketing', 'dashboard'] });
    },
  });
};

// Fonction de vérification multi-tenant (inchangée)
const canDeleteCampaign = async (campaignId: string, businessId: string): Promise<{
  canDelete: boolean;
  reason?: string;
  prestationCount?: number;
  productSaleCount?: number;
}> => {
  console.log('Vérification suppression campagne:', campaignId);
  
  try {
    const campaignRef = doc(db, COLLECTIONS.campaigns, campaignId);
    const campaignSnap = await getDoc(campaignRef);
    
    if (!campaignSnap.exists()) {
      return { canDelete: false, reason: 'Campagne introuvable' };
    }
    
    const campaign = campaignSnap.data();
    if (campaign.businessId !== businessId) {
      return { canDelete: false, reason: 'Campagne non autorisée' };
    }

    const promotionsQuery = query(
      collection(db, COLLECTIONS.promotions),
      where('businessId', '==', businessId),
      where('campaignId', '==', campaignId)
    );
    const promotionsSnap = await getDocs(promotionsQuery).catch(error => {
      handleFirestoreError(error, 'useMarketingCampaigns - Promotions query');
      throw error;
    });
    const promotionIds = promotionsSnap.docs.map(doc => doc.id);
    
    console.log('Promotions trouvées:', promotionIds.length);
    
    if (promotionIds.length === 0) {
      console.log('Aucune promotion, suppression OK');
      return { canDelete: true };
    }
    
    for (const promotionId of promotionIds) {
      const prestationsQuery = query(
        collection(db, COLLECTIONS.prestations),
        where('businessId', '==', businessId),
        where('promotionId', '==', promotionId)
      );
      
      const prestationsSnap = await getDocs(prestationsQuery).catch(error => {
        handleFirestoreError(error, 'useMarketingCampaigns - Prestations query');
        throw error;
      });
      
      if (prestationsSnap.docs.length > 0) {
        console.log('Prestations trouvées, BLOCAGE');
        return {
          canDelete: false,
          reason: "Cette campagne est liée à des prestations/ventes",
          prestationCount: prestationsSnap.docs.length
        };
      }

      const productSalesQuery = query(
        collection(db, COLLECTIONS.productSales),
        where('businessId', '==', businessId),
        where('promotionId', '==', promotionId)
      );

      const productSalesSnap = await getDocs(productSalesQuery).catch(error => {
        handleFirestoreError(error, 'useMarketingCampaigns - ProductSales query');
        throw error;
      });

      if (productSalesSnap.docs.length > 0) {
        console.log('Ventes produits trouvées, BLOCAGE');
        return {
          canDelete: false,
          reason: "Cette campagne est liée à des prestations/ventes",
          productSaleCount: productSalesSnap.docs.length
        };
      }
    }
    
    console.log('Aucune prestation liée');
    return { canDelete: true };
    
  } catch (error) {
    console.error('Erreur lors de la vérification:', error);
    throw error;
  }
};

export const useDeleteCampaign = () => {
  const queryClient = useQueryClient();
  const { currentBusiness } = useAuth();
const businessId = currentBusiness?.id;
  
  return useMutation({
    mutationFn: async (campaignId: string) => {
      console.log('Début suppression campagne:', campaignId, 'businessId', businessId);
      
      if (!businessId) {
        throw new Error('Institut non défini');
      }
    
      try {
        const { canDelete, reason, prestationCount, productSaleCount } = await canDeleteCampaign(campaignId, businessId);
        console.log('Vérification suppression:', { canDelete, reason, prestationCount, productSaleCount });
        
        if (!canDelete) {
          const linked = (prestationCount || 0) + (productSaleCount || 0);
          throw new Error(reason + (linked ? ` (${linked} operation(s) affectee(s))` : ''));
        }
        
        console.log('Recherche promotions à supprimer...');
        const promotionsQuery = query(
          collection(db, COLLECTIONS.promotions),
          where('businessId', '==', businessId),
          where('campaignId', '==', campaignId)
        );
        
        const promotionsSnap = await getDocs(promotionsQuery).catch(error => {
      handleFirestoreError(error, 'useMarketingCampaigns - Promotions query');
      throw error;
    });
        console.log('Promotions trouvées:', promotionsSnap.size);
        
        if (promotionsSnap.size > 0) {
          console.log('Suppression des promotions...');
          const batch = writeBatch(db);
          
          promotionsSnap.docs.forEach((docSnap) => {
            batch.delete(docSnap.ref);
          });
          
          await batch.commit();
          console.log('Promotions supprimées');
        }
        
        console.log('Suppression campagne...');
        const campaignRef = doc(db, COLLECTIONS.campaigns, campaignId);
        await deleteDoc(campaignRef);
        console.log('Campagne supprimée');
        
        return { 
          deletedCampaign: campaignId,
          deletedPromotions: promotionsSnap.size 
        };
        
      } catch (error) {
        console.error('Erreur lors de la suppression:', error);
        
        if (error instanceof Error) {
          throw error;
        }
        
        if (error instanceof FirebaseError) {
          if (error.code === 'permission-denied') {
            throw new Error('Permissions insuffisantes. Vérifiez que vous êtes administrateur.');
          }
          throw new Error(`Erreur Firebase: ${error.message}`);
        }
        
        throw new Error('Erreur inconnue lors de la suppression');
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['marketing', 'campaigns', businessId] });
      queryClient.invalidateQueries({ queryKey: ['marketing', 'promotions'] });
      queryClient.invalidateQueries({ queryKey: ['marketing', 'dashboard'] });
    },
  });
};

export { canDeleteCampaign };

// ===== HOOKS POUR LES PROMOTIONS/MESSAGES =====

export const usePromotionMessages = (date?: Date) => {
  const { currentBusiness } = useAuth();
  const permissions = usePermissions();
  const businessId = currentBusiness?.id;

  // Stabiliser la date pour la query key
  const stableDate = useMemo(() => {
    if (!date) return null;
    const stable = new Date(date);
    stable.setHours(0, 0, 0, 0); // Fixer à 00h00
    return stable.toDateString(); // Seulement jour/mois/année
  }, [date?.toDateString()]); // Dépendance stable

  return useQuery({
    queryKey: ['marketing', 'promotions', 'messages', businessId, stableDate],
    queryFn: async () => {
      if (!businessId) {
        console.warn('Aucun businessId disponible');
        return [];
      }

      try {
        let q = query(
          collection(db, COLLECTIONS.promotions),
          where('businessId', '==', businessId),
          orderBy('createdAt', 'desc')
        );

        if (date) {
          const startOfDay = new Date(date);
          startOfDay.setHours(0, 0, 0, 0);
          const endOfDay = new Date(date);
          endOfDay.setHours(23, 59, 59, 999);

          q = query(
            q,
            where('createdAt', '>=', Timestamp.fromDate(startOfDay)),
            where('createdAt', '<=', Timestamp.fromDate(endOfDay))
          );
        }

        const snapshot = await getDocs(q).catch(error => {
          handleFirestoreError(error, 'useMarketingCampaigns - Main query');
          throw error;
        });
        
        return snapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            createdAt: data.createdAt?.toDate(),
            validUntil: data.validUntil?.toDate(),
            sentAt: data.sentAt?.toDate(),
            usedAt: data.usedAt?.toDate(),
            lastUsedAt: data.lastUsedAt?.toDate(),  // NOUVEAU
            usageCount: data.usageCount || 0,       // NOUVEAU avec fallback
          };
        }) as ClientPromotion[];
      } catch (error) {
        console.warn('Promotions collection not ready:', error);
        return [];
      }
    },
    enabled: !!businessId && (permissions?.canViewMarketing ?? false),
    refetchOnWindowFocus: false,
    staleTime: 30000,
  });
};

export const useMarkPromotionAsSent = () => {
  const queryClient = useQueryClient();
  const { currentBusiness } = useAuth();
const businessId = currentBusiness?.id;
  
  return useMutation({
    mutationFn: async (promotionId: string) => {
      const docRef = doc(db, COLLECTIONS.promotions, promotionId);
      const promotionDoc = await getDoc(docRef);
      
      if (!promotionDoc.exists()) {
        throw new Error('Promotion introuvable');
      }

      const promotionData = promotionDoc.data();
      
      if (promotionData.businessId !== businessId) {
        throw new Error('Cette promotion n\'appartient pas à votre business');
      }

      // Idempotent : si deja marque envoye, ne pas reincrementer les stats
      if (promotionData.sentAt) {
        return promotionId;
      }

      await updateDoc(docRef, {
        sentAt: Timestamp.now(),
      });

      const campaignRef = doc(db, COLLECTIONS.campaigns, promotionData.campaignId);
      await updateDoc(campaignRef, {
        'stats.messagesSent': increment(1),
        lastSentAt: Timestamp.now(),
      });

      return promotionId;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['marketing', 'promotions'] });
      queryClient.invalidateQueries({ queryKey: ['marketing', 'campaigns', businessId] });
      queryClient.invalidateQueries({ queryKey: ['marketing', 'dashboard'] });
    },
  });
};

export const useRefreshCampaignPromotionsFromTemplate = () => {
  const queryClient = useQueryClient();
  const { currentBusiness } = useAuth();
  const businessId = currentBusiness?.id;

  return useMutation({
    mutationFn: async (campaignId: string) => {
      if (!businessId) {
        throw new Error('Institut non défini');
      }

      const campaignRef = doc(db, COLLECTIONS.campaigns, campaignId);
      const campaignSnap = await getDoc(campaignRef);
      if (!campaignSnap.exists()) {
        throw new Error('Campagne introuvable');
      }

      const campaignData = campaignSnap.data();
      if (campaignData.businessId !== businessId) {
        throw new Error("Cette campagne n'appartient pas à votre business");
      }

      const messageTemplate = String(campaignData.messageTemplate || '').trim();
      if (!messageTemplate) {
        throw new Error('Aucun template de message défini sur la campagne');
      }

      const discountType = campaignData.offerDetails?.discountType;
      const discountValueRaw = Number(campaignData.offerDetails?.discountValue || 0);
      const validUntilDate = campaignData.offerDetails?.endDate?.toDate?.() || new Date();
      const validUntil = dayjs(validUntilDate).format('DD/MM/YYYY');
      const discountValueText =
        discountType === 'fixed' ? `${formatCurrency(discountValueRaw, 0)}` : String(discountValueRaw);

      const promotionsQuery = query(
        collection(db, COLLECTIONS.promotions),
        where('businessId', '==', businessId),
        where('campaignId', '==', campaignId),
      );
      const promotionsSnap = await getDocs(promotionsQuery).catch((error) => {
        handleFirestoreError(error, 'useMarketingCampaigns - Refresh template promotions query');
        throw error;
      });

      if (promotionsSnap.empty) {
        return { updatedCount: 0 };
      }

      const docs = promotionsSnap.docs;
      const BATCH_SIZE = 450;
      let updatedCount = 0;

      for (let i = 0; i < docs.length; i += BATCH_SIZE) {
        const chunk = docs.slice(i, i + BATCH_SIZE);
        const batch = writeBatch(db);

        chunk.forEach((docSnap) => {
          const data = docSnap.data() as Record<string, unknown>;
          const variables = {
            clientName: String(data.clientName || 'Client'),
            discountValue: discountValueText,
            discountAmount: discountType === 'fixed' ? discountValueRaw : 0,
            validUntil,
            loyaltyBalance: Number(data.loyaltyBalance || 0),
            lastVisit: String(data.lastVisit || ''),
            businessName: currentBusiness?.name || 'Institut',
            eventName: String(campaignData.name || ''),
            age: Number(data.age || 0),
          };

          const nextMessage = replaceTemplateVariables(messageTemplate, variables);

          batch.update(docSnap.ref, {
            messageText: nextMessage,
            sentAt: null,
            updatedAt: Timestamp.now(),
          });
        });

        await batch.commit();
        updatedCount += chunk.length;
      }

      await updateDoc(campaignRef, {
        'stats.messagesSent': 0,
        lastSentAt: deleteField(),
        updatedAt: Timestamp.now(),
      });

      return { updatedCount };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['marketing', 'promotions'] });
      queryClient.invalidateQueries({ queryKey: ['marketing', 'campaigns', businessId] });
      queryClient.invalidateQueries({ queryKey: ['marketing', 'dashboard'] });
    },
  });
};

// ===== HOOKS POUR LES PROMOTIONS CLIENTS =====

// MODIFIÉ : Logique différenciée selon usageType
export const usePromotions = (clientId?: string) => {
  const { currentBusiness } = useAuth();
  const permissions = usePermissions();
  const businessId = currentBusiness?.id;

  return useQuery({
    queryKey: ['marketing', 'promotions', businessId, clientId],
    queryFn: async () => {
      console.log('=== DEBUG usePromotions avec usageType ===');
      console.log('clientId reçu:', clientId);
      console.log('businessId:', businessId);
      
      if (!clientId || !businessId) {
        console.log('Pas de clientId ou businessId, retour []');
        return [];
      }

      try {
        // Récupérer les campagnes actives avec leur usageType
        const activeCampaignsQuery = query(
          collection(db, COLLECTIONS.campaigns),
          where('businessId', '==', businessId),
          where('status', '==', 'active')
        );
        const activeCampaignsSnap = await getDocs(activeCampaignsQuery).catch(error => {
          handleFirestoreError(error, 'useMarketingCampaigns - Active campaigns query');
          throw error;
        });
        const activeCampaigns = activeCampaignsSnap.docs.map(doc => ({
          id: doc.id,
          usageType: doc.data().usageType || 'single_use',  // NOUVEAU : fallback pour migration
          ...doc.data()
        }));
        
        console.log('Campagnes actives trouvées:', activeCampaigns.length);

        if (activeCampaigns.length === 0) {
          console.log('Aucune campagne active, retour []');
          return [];
        }

        const activeCampaignIds = activeCampaigns.map(c => c.id);

        // Récupérer toutes les promotions du client pour ces campagnes
        let q = query(
          collection(db, COLLECTIONS.promotions),
          where('businessId', '==', businessId),
          where('active', '==', true),
          where('campaignId', 'in', activeCampaignIds),
          where('clientId', '==', clientId),
          where('sentAt', '!=', null),
          orderBy('createdAt', 'desc')
        );

        const snapshot = await getDocs(q).catch(error => {
          handleFirestoreError(error, 'useMarketingCampaigns - Main query');
          throw error;
        });
        console.log('Promotions trouvées:', snapshot.size);

        const promotions = snapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            createdAt: data.createdAt?.toDate(),
            validUntil: data.validUntil?.toDate(),
            sentAt: data.sentAt?.toDate(),
            usedAt: data.usedAt?.toDate(),
            lastUsedAt: data.lastUsedAt?.toDate(),
            usageCount: data.usageCount || 0,
          };
        }) as ClientPromotion[];

        // NOUVEAU : Filtrage selon usageType
        const now = new Date();
        const filtered = promotions.filter(promo => {
          const campaign = activeCampaigns.find(c => c.id === promo.campaignId);
          if (!campaign) return false;

          const usageType = campaign.usageType || 'single_use';
          console.log(`Promotion ${promo.id}: usageType=${usageType}, used=${promo.used}, validUntil=${promo.validUntil}`);

          if (usageType === 'single_use') {
            // Comportement actuel : pas utilisée ET dans la période
            return !promo.used && promo.validUntil && promo.validUntil > now;
          } else {
            // Nouveau comportement : seulement dans la période (ignore used)
            return promo.validUntil && promo.validUntil > now;
          }
        });
        
        console.log('Promotions après filtrage par usageType:', filtered.length);
        console.log('=================================');
        
        return filtered;

      } catch (error) {
        console.error('Erreur dans usePromotions:', error);
        return [];
      }
    },
    enabled: !!clientId && !!businessId && (permissions?.canViewMarketing ?? false),
  });
};

// MODIFIÉ : Logique d'application différenciée selon usageType
// Fonction utilitaire pour synchroniser les conversions d'une campagne
const syncCampaignConversions = async (campaignId: string, businessId: string) => {
  console.log(`🔄 Synchronisation des conversions pour campagne ${campaignId}`);
  
  // 1. Récupérer toutes les promotions de cette campagne
  const promotionsQuery = query(
    collection(db, COLLECTIONS.promotions),
    where('campaignId', '==', campaignId),
    where('businessId', '==', businessId)
  );
  
        const promotionsSnap = await getDocs(promotionsQuery).catch(error => {
          handleFirestoreError(error, 'useMarketingCampaigns - Promotions query');
          throw error;
        });
  const promotions = promotionsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() } as any));
  
  // 2. Compter les promotions réellement converties
  // single_use: used === true ; period_based: usageCount > 0
  const usedPromotionsCount = promotions.filter((promo) => {
    const usageCount = Number(promo.usageCount || 0);
    return promo.used === true || usageCount > 0;
  }).length;
  
  // 3. Mettre à jour le compteur de conversions de la campagne
  const campaignRef = doc(db, COLLECTIONS.campaigns, campaignId);
  await updateDoc(campaignRef, {
    'stats.conversionsCount': usedPromotionsCount,
  });
  
  console.log(`✅ Conversions synchronisées: ${usedPromotionsCount} pour campagne ${campaignId}`);
  return usedPromotionsCount;
};

export const useApplyPromotion = () => {
  const queryClient = useQueryClient();
  const { currentBusiness } = useAuth();
const businessId = currentBusiness?.id;
  
  return useMutation({
    mutationFn: async ({ 
      promotionId, 
      prestationId,
      revenueAmount,
    }: { 
      promotionId: string; 
      prestationId: string;
      revenueAmount: number;
    }) => {
      const promotionRef = doc(db, COLLECTIONS.promotions, promotionId);
      const promotionDoc = await getDoc(promotionRef);
      
      if (!promotionDoc.exists()) {
        throw new Error('Promotion introuvable');
      }

      const promotionData = promotionDoc.data();
      
      if (promotionData.businessId !== businessId) {
        throw new Error('Cette promotion n\'appartient pas à votre business');
      }

      // Récupérer la campagne pour connaître le usageType
      const campaignRef = doc(db, COLLECTIONS.campaigns, promotionData.campaignId);
      const campaignDoc = await getDoc(campaignRef);
      
      if (!campaignDoc.exists()) {
        throw new Error('Campagne associée introuvable');
      }

      const campaignData = campaignDoc.data();
      const usageType = campaignData.usageType || 'single_use';
      
      console.log(`Application promotion ${promotionId}: usageType=${usageType}`);

      // NOUVEAU : Logique différenciée selon usageType
      if (usageType === 'single_use') {
        // Comportement actuel : marquer comme utilisée
        await updateDoc(promotionRef, {
          used: true,
          usedAt: Timestamp.now(),
          prestationId,
          active: false,
          usageCount: (promotionData.usageCount || 0) + 1,  // Incrémenter pour stats
          lastUsedAt: Timestamp.now(),
        });
        
        // NOUVEAU : Synchroniser les conversions au lieu d'incrémenter
        if (promotionData.campaignId && businessId) {
          await syncCampaignConversions(promotionData.campaignId as string, businessId);
        }
        
        // Toujours incrémenter le revenu
        await updateDoc(campaignRef, {
          'stats.revenueGenerated': increment(revenueAmount),
        });
        
      } else {
        // Nouveau comportement : garder active, incrémenter usage
        await updateDoc(promotionRef, {
          used: false,  // Reste false !
          usageCount: (promotionData.usageCount || 0) + 1,
          lastUsedAt: Timestamp.now(),
          // Pas de prestationId car réutilisable
        });
        
        // NOUVEAU : Synchroniser les conversions au lieu d'incrémenter
        if (promotionData.campaignId && businessId) {
          await syncCampaignConversions(promotionData.campaignId as string, businessId);
        }
        
        // Toujours incrémenter le revenu
        await updateDoc(campaignRef, {
          'stats.revenueGenerated': increment(revenueAmount),
        });
      }

      return promotionId;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['marketing', 'promotions'] });
      queryClient.invalidateQueries({ queryKey: ['marketing', 'campaigns', businessId] });
      queryClient.invalidateQueries({ queryKey: ['marketing', 'dashboard'] });
    },
  });
};

// ===== HOOK POUR GÉNÉRER LES PROMOTIONS/MESSAGES =====

export const useGenerateMessages = () => {
  const queryClient = useQueryClient();
  const { currentBusiness } = useAuth();
const businessId = currentBusiness?.id;
  
  return useMutation({
    mutationFn: async ({ 
      campaignId, 
      targetClients 
    }: { 
      campaignId: string; 
      targetClients: Client[]; 
    }) => {
      if (!businessId) {
        throw new Error('Institut non défini');
      }

      try {
        const campaignDoc = await getDoc(doc(db, COLLECTIONS.campaigns, campaignId));
        if (!campaignDoc.exists()) {
          throw new Error('Campagne introuvable');
        }
        
        const campaignData = campaignDoc.data();
        
        if (campaignData.businessId !== businessId) {
          throw new Error('Cette campagne n\'appartient pas à votre business');
        }
        
        const campaign = convertFirestoreToCampaign(campaignDoc.id, campaignData);

        const invalidClients = targetClients.filter(client => client.businessId !== businessId);
        if (invalidClients.length > 0) {
          throw new Error(`${invalidClients.length} client(s) n'appartiennent pas à votre business`);
        }

        const prestationsQuery = query(
          collection(db, COLLECTIONS.prestations),
          where('businessId', '==', businessId)
        );
        const prestationsSnap = await getDocs(prestationsQuery).catch(error => {
        handleFirestoreError(error, 'useMarketingCampaigns - Prestations query');
        throw error;
      });
        const prestations = prestationsSnap.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          date: doc.data().date?.toDate(),
        })) as Prestation[];

        const clientLastVisits: Record<string, Date> = {};
        targetClients.forEach(client => {
          const clientPrestations = prestations
            .filter(p => p.clientId === client.id)
            .sort((a, b) => dayjs(b.date).unix() - dayjs(a.date).unix());
          
          if (clientPrestations.length > 0) {
            clientLastVisits[client.id] = clientPrestations[0].date;
          }
        });

        const promotions = generatePromotionsForCampaign(
          campaign,
          targetClients,
          currentBusiness?.name || "Institut",
          clientLastVisits
        );

        // NOUVEAU : Ajouter usageCount initial selon usageType
        const promotionPromises = promotions.map(async (promotionData) => {
          const promotionDocData = {
            ...promotionData,
            businessId,
            usageCount: 0,  // NOUVEAU : Initialiser à 0
            createdAt: Timestamp.fromDate(promotionData.createdAt),
            validUntil: Timestamp.fromDate(promotionData.validUntil),
            ...(promotionData.applicableServices && promotionData.applicableServices.length > 0 && {
              applicableServices: promotionData.applicableServices,
            }),
            ...(promotionData.applicableProducts && promotionData.applicableProducts.length > 0 && {
              applicableProducts: promotionData.applicableProducts,
            }),
          };
          
          return await addDoc(
            collection(db, COLLECTIONS.promotions), 
            promotionDocData
          );
        });

        await Promise.all(promotionPromises);

        const campaignRef = doc(db, COLLECTIONS.campaigns, campaignId);
        await updateDoc(campaignRef, {
          'stats.messagesGenerated': increment(promotions.length),
        });

        return {
          messagesCount: promotions.length,
          promotionsCount: promotions.length,
        };

      } catch (error) {
        console.error('Erreur generation messages:', error);
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['marketing', 'promotions'] });
      queryClient.invalidateQueries({ queryKey: ['marketing', 'campaigns', businessId] });
      queryClient.invalidateQueries({ queryKey: ['marketing', 'dashboard'] });
    },
  });
};

// ===== HOOK POUR LE DASHBOARD MARKETING =====

export const useMarketingDashboard = () => {
  const { currentBusiness } = useAuth();
  const permissions = usePermissions();
  const businessId = currentBusiness?.id;

  return useQuery({
    queryKey: ['marketing', 'dashboard', businessId],
    queryFn: async (): Promise<MarketingDashboardData> => {
      if (!businessId) {
        return {
          activeCampaigns: 0,
          pendingMessages: 0,
          totalConversions: 0,
          totalRevenue: 0,
          topPerformingCampaign: undefined,
        };
      }

      try {
        const campaignsQuery = query(
          collection(db, COLLECTIONS.campaigns),
          where('businessId', '==', businessId),
          orderBy('createdAt', 'desc')
        );
        const campaignsSnap = await getDocs(campaignsQuery).catch(error => {
          handleFirestoreError(error, 'useMarketingCampaigns - Campaigns query');
          throw error;
        });
        
        const allCampaigns = campaignsSnap.docs.map(doc => 
          convertFirestoreToCampaign(doc.id, doc.data())
        );
        
        const activeCampaigns = allCampaigns.filter(campaign => {
          const computedStatus = getComputedCampaignStatus(campaign);
          return computedStatus === 'active' && campaign.status === 'active';
        });

        const today = new Date();
        const startOfDay = new Date(today);
        startOfDay.setHours(0, 0, 0, 0);
        const endOfDay = new Date(today);
        endOfDay.setHours(23, 59, 59, 999);

        const todayPromotionsQuery = query(
          collection(db, COLLECTIONS.promotions),
          where('businessId', '==', businessId),
          where('createdAt', '>=', Timestamp.fromDate(startOfDay)),
          where('createdAt', '<=', Timestamp.fromDate(endOfDay)),
          where('sentAt', '==', null)
        );
        const todayPromotionsSnap = await getDocs(todayPromotionsQuery).catch(error => {
          handleFirestoreError(error, 'useMarketingCampaigns - Today promotions query');
          throw error;
        });

        // MODIFIÉ : Calcul des stats selon usageType
        const totalConversions = allCampaigns.reduce((sum, camp) => {
          // Pour l'instant, on garde le calcul simple
          // TODO: Améliorer avec les vraies stats différenciées
          return sum + (camp.stats?.conversionsCount || 0);
        }, 0);
        
        const totalRevenue = allCampaigns.reduce((sum, camp) => sum + (camp.stats?.revenueGenerated || 0), 0);

        const topPerformingCampaign = allCampaigns.reduce((best, current) => {
          const currentConversions = current.stats?.conversionsCount || 0;
          const bestConversions = best?.stats?.conversionsCount || 0;
          return currentConversions > bestConversions ? current : best;
        }, allCampaigns[0]);

        return {
          activeCampaigns: activeCampaigns.length,
          pendingMessages: todayPromotionsSnap.size,
          totalConversions,
          totalRevenue,
          topPerformingCampaign: topPerformingCampaign ? {
            name: topPerformingCampaign.name,
            conversions: topPerformingCampaign.stats?.conversionsCount || 0,
            revenue: topPerformingCampaign.stats?.revenueGenerated || 0,
          } : undefined,
        };
      } catch (error) {
        console.warn('Dashboard data not ready:', error);
        return {
          activeCampaigns: 0,
          pendingMessages: 0,
          totalConversions: 0,
          totalRevenue: 0,
          topPerformingCampaign: undefined,
        };
      }
    },
    enabled: !!businessId && (permissions?.canViewMarketing ?? false),
    retry: (failureCount, error: any) => {
      if (error.message?.includes('requires an index')) {
        return false;
      }
      return failureCount < 2;
    },
  });
};

// ===== FONCTIONS UTILITAIRES DE CONVERSION =====

const convertFirestoreToCampaign = (id: string, data: any): Campaign => {
  return {
    id,
    businessId: data.businessId,
    name: data.name,
    description: data.description,
    type: data.type,
    objective: data.objective,
    status: data.status,
    usageType: data.usageType || 'single_use',  // NOUVEAU : Fallback pour migration
    createdBy: data.createdBy,
    stats: data.stats,
    messageTemplate: data.messageTemplate,
    channel: data.channel,
    autoSend: data.autoSend,
    createdAt: data.createdAt?.toDate(),
    updatedAt: data.updatedAt?.toDate(),
    lastSentAt: data.lastSentAt?.toDate(),
    startDate: data.startDate?.toDate(),
    offerDetails: {
      ...data.offerDetails,
      endDate: data.offerDetails?.endDate?.toDate(),
      ...(data.offerDetails?.validityDays && !data.offerDetails?.endDate && {
        endDate: dayjs(data.startDate?.toDate() || data.createdAt?.toDate())
          .add(data.offerDetails.validityDays, 'days')
          .toDate(),
      }),
    },
    targetCriteria: convertTargetCriteriaFromFirestore(data.targetCriteria || {}),
  };
};

const convertTargetCriteriaToFirestore = (criteria: any) => {
  const result: any = { ...criteria };
  
  if (criteria.birthdayPeriod) {
    result.birthdayPeriod = {
      startDate: Timestamp.fromDate(criteria.birthdayPeriod.startDate),
      endDate: Timestamp.fromDate(criteria.birthdayPeriod.endDate),
    };
  }
  
  return result;
};

const convertTargetCriteriaFromFirestore = (data: any) => {
  const result: any = { ...data };
  
  if (data.birthdayPeriod) {
    result.birthdayPeriod = {
      startDate: data.birthdayPeriod.startDate?.toDate(),
      endDate: data.birthdayPeriod.endDate?.toDate(),
    };
  }
  
  return result;
};